/* STEP 7e-2 · S3 — the firm deviation report on the Methodology page (manager roles only: the page shows it when GET /review-roles
   admits methodology.approve; the server refuses anyone else, METHODOLOGY_REPORT_FORBIDDEN). Every deviation across the firm's
   engagements with its status; filters by engagement / area / status (GET /methodology-deviations); Export XLSX
   (GET /methodology-deviations/export with the same filters). */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { toErrMsg } from "@/lib/api";
import { asArray } from "@/lib/asArray";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTime } from "@/lib/dt";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import EngagementPicker from "@/components/EngagementPicker";   // Step 11b · N1
import { useAuth } from "@/contexts/AuthContext";

export const STATUS_STYLE = {
  open: "bg-rose-100 text-rose-800", acknowledged: "bg-amber-100 text-amber-900", closed: "bg-slate-100 text-slate-700",
};

export default function MethodologyDeviationReport() {
  const colW = useResizableColumns({ storageKey: "methodology-deviation-report", defaults: [160, 240, 160, 240, 130, 240, 160, 110, 110], mins: [100, 130, 100, 130, 90, 130, 100, 72, 72] });   // Step 11d · add-on
  const { t, lang } = useI18n();
  const user = (useAuth() || {}).user;   // Step 11b · N1: recent engagements first in the filter
  const [f, setF] = useState({ engagement_id: "", area: "", status: "" });
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/methodology-deviations", { params: f, _silent: true }).then((r) => setData(r.data)).catch((e) => toast.error(toErrMsg(e)));
  }, [f]);
  const exportXlsx = async () => {
    try {
      const r = await api.get("/methodology-deviations/export", { params: f, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "methodology_deviations.xlsx"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(toErrMsg(e)); }
  };
  const rows = asArray(data?.rows);
  const sel = "h-7 rounded border bg-white px-1 text-[12px] dark:bg-slate-900";
  return (
    <Card data-testid="meth-dev-report">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-sm">{t("s7e2.report.title")}</CardTitle>
          <p className="text-xs text-muted-foreground">{t("s7e2.report.intro")}</p>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={exportXlsx} data-testid="meth-dev-export">{t("s7e2.report.export")}</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-[12px]">
          <label>{t("s7e2.report.engagement")}{" "}
            {/* Step 11b · N1: the one searchable engagement picker ("All engagements" first) */}
            <EngagementPicker engagements={data?.engagements} value={f.engagement_id} onChange={(id) => setF({ ...f, engagement_id: id })}
                              allLabel={t("s7e2.report.allEngagements")} userKey={user?.user_id || user?.id}
                              testId="meth-dev-f-eng" optionTestId="meth-dev-f-eng-option"
                              className="inline-block w-72 align-middle" triggerClassName="h-7 text-[12px]" />
          </label>
          <label>{t("s7e2.report.area")}{" "}
            <select className={sel} value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} data-testid="meth-dev-f-area">
              <option value="">{t("s7e2.report.allAreas")}</option>
              {["materiality", "sampling"].map((a) => <option key={a} value={a}>{t(`s7e2.dev.area.${a}`)}</option>)}
            </select>
          </label>
          <label>{t("s7e2.report.status")}{" "}
            <select className={sel} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="meth-dev-f-status">
              <option value="">{t("s7e2.report.allStatuses")}</option>
              {["open", "acknowledged", "closed"].map((s) => <option key={s} value={s}>{t(`s7e2.dev.status.${s}`)}</option>)}
            </select>
          </label>
          {data?.counts && (
            <span className="text-muted-foreground" data-testid="meth-dev-counts">
              {t("s7e2.report.counts").replace("{open}", data.counts.open).replace("{acknowledged}", data.counts.acknowledged).replace("{closed}", data.counts.closed)}
            </span>
          )}
        </div>
        {rows.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="meth-dev-empty">{t("s7e2.report.empty")}</p> : (
          <div className="overflow-auto rounded border">
            <ResizableTable cols={colW} className="w-full text-[12px]" data-testid="meth-dev-table">
              <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground dark:bg-slate-900">
                <tr>
                  <ResizableTh index={0} className="px-2 py-1 text-start">{t("s7e2.report.engagement")}</ResizableTh>
                  <ResizableTh index={1} className="px-2 py-1 text-start">{t("s7e2.dev.colItem")}</ResizableTh>
                  <ResizableTh index={2} className="px-2 py-1 text-start">{t("s7e2.dev.colSetting")}</ResizableTh>
                  <ResizableTh index={3} className="px-2 py-1 text-start">{t("s7e2.dev.colFirm")}</ResizableTh>
                  <ResizableTh index={4} className="px-2 py-1 text-start">{t("s7e2.dev.colValue")}</ResizableTh>
                  <ResizableTh index={5} className="px-2 py-1 text-start">{t("s7e2.dev.colReason")}</ResizableTh>
                  <ResizableTh index={6} className="px-2 py-1 text-start">{t("s7e2.dev.colBy")}</ResizableTh>
                  <ResizableTh index={7} className="px-2 py-1 text-start">{t("s7e2.dev.colVersion")}</ResizableTh>
                  <ResizableTh index={8} className="px-2 py-1 text-start">{t("s7e2.dev.colStatus")}</ResizableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.id} className="align-top" data-testid={`meth-dev-rep-${r.id}`}>
                    <td className="px-2 py-1">{r.client_name}{r.engagement_ref ? <div className="text-[10px] text-muted-foreground">{r.engagement_ref}</div> : null}</td>
                    <td className="px-2 py-1">{t(`s7e2.dev.area.${r.area}`)}{r.area === "sampling" && r.subject_label ? ` — ${r.subject_label}` : ""}</td>
                    <td className="px-2 py-1">{lang === "ar" ? r.setting_label_ar : r.setting_label}</td>
                    <td className="px-2 py-1 font-mono">{lang === "ar" ? r.firm_text_ar : r.firm_text}</td>
                    <td className="px-2 py-1 font-mono">{r.engagement_value} %</td>
                    <td className="px-2 py-1">{r.reason}</td>
                    <td className="px-2 py-1">{r.by}<div className="font-mono text-[10px]">{fmtDateTime(r.at)}</div></td>
                    <td className="px-2 py-1 font-mono">V{r.methodology_version}</td>
                    <td className="px-2 py-1">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] ${STATUS_STYLE[r.display_status] || ""}`} data-testid={`meth-dev-rep-status-${r.id}`}>
                        {t(`s7e2.dev.status.${r.display_status}`)}
                      </span>
                      {r.acknowledged_by && <div className="text-[10px] text-muted-foreground">{t("s7e2.dev.acked").replace("{by}", r.acknowledged_by).replace("{at}", fmtDateTime(r.acknowledged_at))}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
